// src/api/utils/format.ts
// Consolidated format utilities with boundary guards — FR-003.
// Every function handles null, undefined, NaN, negative, zero, and extreme
// values without throwing.  The em-dash "—" is the universal fallback for
// un-displayable values.

const DASH = "\u2014"; // em-dash

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true when the value is a finite number (rejects null/undefined/NaN/Infinity). */
function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v);
}

// ---------------------------------------------------------------------------
// formatDuration
// ---------------------------------------------------------------------------

/**
 * Format milliseconds into a human-readable duration string.
 *
 * - NaN / null / undefined / non-finite  -> "—"
 * - Negative                              -> "—"
 * - 0                                     -> "0ms"
 * - < 1000                                -> "<n>ms"
 * - < 60 000                              -> "<n>s"
 * - >= 60 000                             -> "Xh Ym Zs" (omitting zero parts)
 */
export function formatDuration(ms: number | null | undefined): string {
  if (!isFiniteNumber(ms) || ms < 0) return DASH;
  if (ms === 0) return "0ms";
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return seconds > 0
      ? `${hours}h ${minutes}m ${seconds}s`
      : minutes > 0
        ? `${hours}h ${minutes}m`
        : `${hours}h`;
  }
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

// ---------------------------------------------------------------------------
// timeAgo
// ---------------------------------------------------------------------------

/**
 * Format a timestamp into a relative time string.
 *
 * - NaN / null / undefined / non-finite  -> "—"
 * - Future timestamp (diff < 0)          -> "just now"
 * - < 60 s                               -> "just now"
 * - < 1 h                                -> "<n>m ago"
 * - < 24 h                               -> "<n>h ago"
 * - >= 24 h                              -> locale date string
 */
export function timeAgo(ts: number | null | undefined): string {
  if (!isFiniteNumber(ts)) return DASH;

  const diff = Date.now() - ts;

  // Future timestamp or within the last minute — smooth 1ms boundary
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ts).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// formatCost
// ---------------------------------------------------------------------------

/**
 * Format a USD amount with symbol, comma separators, and appropriate decimals.
 *
 * - null / undefined / NaN / non-finite  -> "—"
 * - Negative                             -> "—"
 * - 0                                    -> "$0.00"
 * - < 0.01                               -> "$0.0050" (4 decimals for small amounts)
 * - >= 0.01                              -> "$1,234.56" (2 decimals, locale-formatted)
 */
export function formatCost(usd: number | null | undefined): string {
  if (!isFiniteNumber(usd) || usd < 0) return DASH;
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ---------------------------------------------------------------------------
// formatTokens
// ---------------------------------------------------------------------------

/**
 * Format a token count into a compact string with M/K suffix.
 *
 * - null / undefined / NaN / non-finite  -> "—"
 * - Negative                             -> "—"
 * - 0                                    -> "0"
 * - Sub-1000: uses Math.floor (999.5 -> "999", NOT rounded to "1K")
 * - >= 1 000 000                         -> "1.5M"
 * - >= 1 000                             -> "1.5K"
 */
export function formatTokens(count: number | null | undefined): string {
  if (!isFiniteNumber(count) || count < 0) return DASH;
  if (count >= 1_000_000) {
    const millions = count / 1_000_000;
    return `${parseFloat(millions.toFixed(1))}M`;
  }
  if (count >= 1_000) {
    const thousands = count / 1_000;
    return `${parseFloat(thousands.toFixed(1))}K`;
  }
  return `${Math.floor(count)}`;
}

// ---------------------------------------------------------------------------
// formatPercent
// ---------------------------------------------------------------------------

/**
 * Format a decimal fraction as a percentage string, clamped to [0, 100].
 *
 * - null / undefined / NaN / non-finite  -> "—"
 * - Negative                             -> "0.0%"  (clamped)
 * - > 1.0                                -> "100.0%" (clamped)
 * - Normal                               -> "45.6%"
 */
export function formatPercent(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH;
  const clamped = Math.min(Math.max(value, 0), 1);
  return `${(clamped * 100).toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// formatBytes
// ---------------------------------------------------------------------------

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB", "PB"] as const;

/**
 * Format a byte count into a human-readable string with standard SI units.
 *
 * - null / undefined / NaN / non-finite  -> "—"
 * - Negative                             -> "—"
 * - 0                                    -> "0 B"
 * - Normal                               -> "1.5 MB"
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (!isFiniteNumber(bytes) || bytes < 0) return DASH;
  if (bytes === 0) return "0 B";

  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    BYTE_UNITS.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  const unit = BYTE_UNITS[exponent];

  // Show decimals only when there's a fractional part worth displaying
  const formatted = value % 1 === 0 ? `${value}` : `${parseFloat(value.toFixed(2))}`;
  return `${formatted} ${unit}`;
}
